import { useEffect, useState } from 'react';

/**
 * Which of the given element ids is the current section while the page
 * scrolls: the first one (in the order given) that is inside the band near
 * the top of the viewport, else the last one that was. Built on
 * IntersectionObserver, no dependency. Without IntersectionObserver (old
 * browsers, jsdom) it returns the initial id and never changes.
 *
 * @param {string[]} ids - section element ids, in document order
 * @param {string|null} [initial]
 * @returns {[string|null, (id: string) => void]} the active id, and a setter
 *   for when the reader jumps to a section (a click) before the observer
 *   has reported it
 */
export function useScrollSpy(ids, initial = null) {
  const [active, setActive] = useState(initial);
  const key = ids.join('|');

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const elements = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (elements.length === 0) return undefined;

    const visible = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        const first = ids.find((id) => visible.has(id));
        if (first) setActive(first);
      },
      // The band from 10% to 40% below the top edge: a section becomes
      // current once its top passes the upper part of the screen.
      { rootMargin: '-10% 0px -60% 0px', threshold: 0 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // `key` stands for the ids array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return [active, setActive];
}
